import React, {useState, useContext} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {Banner, Button, WithTooltip} from '@albert-io/atomic';
import {pluralize} from 'lib/stringUtils';
import {getModelForResourceType} from 'resources/modelRegistry';
import {stats} from 'lib/StatsUtil';

import StagingDetails from 'components/PracticeView/StagingDetails/StagingDetails.react';

import SubmissionStatusBadge from '../SubmissionStatusBadge/SubmissionStatusBadge.react';
import {
  startAssignment,
  correctAnswerSettings,
  statusTypes,
  AssignmentContext
} from '../assignmentPracticeView.utils';
import SubmitAssignmentButton, {
  UnsafeWithoutConfirmSubmitAssignmentButton
} from '../SubmitAssignmentButton/SubmitAssignmentButton.react';

import './assignment-details.scss';

const assignmentPropType = PropTypes.instanceOf(getModelForResourceType('assignment_v3'));

const AssignmentDetails = ({onDismissDetails, invalidateAssignment}) => {
  const {status, isGraded, isOutOfTime, assignment, classroom, classroomId, studentId} =
    useContext(AssignmentContext);
  const isSubmitted = status === statusTypes.submitted;
  const [areDetailsExpanded, setAreDetailsExpanded] = useState(
    !isSubmitted && !isGraded && !isOutOfTime
  );
  const idsToSelect = {
    classroomId,
    studentId
  };
  return (
    <StagingDetails className='assignment-details-v2'>
      <StagingDetails.Header
        title={assignment.getName()}
        subtitle={`${classroom.getName()} with ${assignment.getTeacher().getDisplayName('s l')}`}
        badge={<SubmissionStatusBadge status={status} />}
      />
      <StatusWarningMessage />
      <StagingDetails.Body>
        {isSubmitted && <SubmittedAt />}
        {!isGraded && isSubmitted && <Explanations />}
        {isGraded && <Results />}
        {areDetailsExpanded ? (
          <>
            <Due {...idsToSelect} />
            <Content />
            <TimeLimit {...idsToSelect} />
            <Message {...idsToSelect} />
            {(isGraded || !isSubmitted) && <Explanations />}
          </>
        ) : (
          <ShowDetails
            onClick={() => {
              setAreDetailsExpanded(true);
            }}
          />
        )}
      </StagingDetails.Body>
      <FooterActions
        onDismissDetails={onDismissDetails}
        invalidateAssignment={invalidateAssignment}
      />
    </StagingDetails>
  );
};

AssignmentDetails.propTypes = {
  onDismissDetails: PropTypes.func.isRequired,
  invalidateAssignment: PropTypes.func.isRequired
};

export default AssignmentDetails;

/**
 * Body detail items
 */

const StatusWarningMessage = () => {
  const {status, isGraded, isOutOfTime, isAnswerable, isPastDue} = useContext(AssignmentContext);

  if (status === statusTypes.submitted) {
    return null;
  }

  let warning;
  let theme = 'danger';
  if (isOutOfTime && !isGraded) {
    warning = 'You are out of time. Submit the assignment to see your results.';
  } else if (!isAnswerable) {
    warning = 'Assignment is past due and you can no longer answer questions.';
  } else if (isPastDue && isAnswerable) {
    warning = 'Assignment is past due, but you can submit it late.';
    theme = 'default';
  }
  return warning ? (
    <Banner theme={theme} className='u-mar-lr_3 u-mar-t_3 u-mar-b_2'>
      <Banner.Icon icon={['far', 'exclamation-triangle']} /> {warning}
    </Banner>
  ) : null;
};

const SubmittedAt = () => {
  const {assignment, isPastDue} = useContext(AssignmentContext);
  const submitDate = moment(assignment.getMeta().getStudentSubmittedAt()).tz(moment.tz.guess());
  return (
    <StagingDetails.Item
      warn={isPastDue}
      label={isPastDue ? 'Submitted late' : 'Submitted'}
      details={`${submitDate.format('MMMM Do, YYYY')} at ${submitDate.format('h:mm a z')}`}
      icon={['far', 'calendar-alt']}
    />
  );
};

const Explanations = () => {
  const {assignment} = useContext(AssignmentContext);
  const details = {
    [correctAnswerSettings.showAfterDueDate]: 'Explanations will show after the due date passes.',
    [correctAnswerSettings.showAfterAll]:
      'Explanations will show after you submit your assignment.',
    [correctAnswerSettings.showAfterEach]:
      'Explanations will show after you answer each question or submit the assignment.'
  }[assignment.getCorrectAnswerSetting()];
  return (
    <StagingDetails.Item
      label='Results &amp; explanations'
      details={details}
      icon={['fas', 'asterisk']}
    />
  );
};

const Results = () => {
  const {assignment} = useContext(AssignmentContext);
  const correctCount = assignment.getMeta().getStudentCountOfCorrectGuesses();
  const totalCount = assignment.getMeta().getCountOfQuestions();
  const grade = stats.percentage(correctCount, totalCount);
  return (
    <>
      <StagingDetails.Item label='Grade' details={`${grade}%`} icon={['far', 'book']} />
      <StagingDetails.Item
        label='Correct answers'
        details={`${correctCount} of ${totalCount}`}
        icon={['fas', 'check']}
      />
    </>
  );
};

const Due = ({classroomId, studentId}: {classroomId: string, studentId: string}) => {
  const {assignment, status, isPastDue} = useContext(AssignmentContext);
  const dueDate = moment(assignment.getDueDateFromSettings({classroomId, studentId})).tz(
    moment.tz.guess()
  );
  const isSubmitted = status === statusTypes.submitted;
  const shouldMarkAsPastDue = !isSubmitted && isPastDue;
  return (
    <StagingDetails.Item
      warn={shouldMarkAsPastDue}
      label={shouldMarkAsPastDue ? 'Past Due' : 'Due date'}
      details={`${dueDate.format('MMMM Do, YYYY')} at ${dueDate.format('h:mm a z')}`}
      icon={['far', 'calendar-alt']}
    />
  );
};

const Content = () => {
  const {assignment} = useContext(AssignmentContext);
  const questionCount = assignment.getMeta().getCountOfQuestions();
  return (
    <StagingDetails.Item
      label='Content'
      details={`${questionCount} ${pluralize('question', questionCount)}`}
      icon={['far', 'question']}
    />
  );
};

const TimeLimit = ({classroomId, studentId}: {classroomId: string, studentId: string}) => {
  const {assignment, isAnswerable} = useContext(AssignmentContext);
  const timeLimit = assignment.getTimeLimitFromSettings({classroomId, studentId});
  return (
    <StagingDetails.Item
      className='assignment-details-time-limit'
      label='Time limit'
      details={timeLimit ? `${timeLimit} ${pluralize('minute', timeLimit)}` : 'No limit'}
      icon={['far', 'clock']}
      additionalInfo={
        timeLimit && isAnswerable
          ? 'The timer does not stop. It will continue even if you leave the tab or sign out.'
          : null
      }
    />
  );
};

const Message = ({classroomId, studentId}: {classroomId: string, studentId: string}) => {
  const {assignment} = useContext(AssignmentContext);
  const message = assignment.getMessageFromSettings({classroomId, studentId});
  if (!message) {
    return null;
  }
  return (
    <StagingDetails.Item
      className='assignment-details-message'
      label='Message'
      details={`"${message}"`}
      icon={['far', 'comment']}
    />
  );
};

const ShowDetails = ({onClick}) => {
  return (
    <StagingDetails.Item
      onLabelClick={onClick}
      label='Show assignment details'
      icon={['far', 'caret-right']}
    />
  );
};

ShowDetails.propTypes = {
  onClick: PropTypes.func
};

/**
 * Footer actions
 */

class FooterActions extends React.Component {
  static propTypes = {
    onDismissDetails: PropTypes.func,
    invalidateAssignment: PropTypes.func
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      isActionPending: false
    };
  }

  static contextType = AssignmentContext;

  handleActionStart = () => {
    this.setState({
      isActionPending: true
    });
  };

  render() {
    const {assignment, status, isGraded, isOutOfTime, isAnswerable} = this.context;
    const {isActionPending} = this.state;
    const {onDismissDetails, invalidateAssignment} = this.props;
    const actions = [];
    if (status === statusTypes.notStarted && !isGraded) {
      actions.push(
        <StartAssignment
          onInit={this.handleActionStart}
          onEnd={onDismissDetails}
          assignment={assignment}
        />
      );
    }
    if (status === statusTypes.inProgress && !isGraded) {
      if (isAnswerable) {
        actions.push(
          <SubmitAssignmentButton
            onInit={this.handleActionStart}
            onEnd={invalidateAssignment}
            onDismiss={onDismissDetails}
          >
            Submit assignment
          </SubmitAssignmentButton>
        );

        actions.push(<Continue onClick={onDismissDetails} />);
      } else {
        actions.push(
          <UnsafeWithoutConfirmSubmitAssignmentButton
            onInit={this.handleActionStart}
            onEnd={invalidateAssignment}
          />
        );
      }
    }

    if (
      /**
       * If the user can't answer questions, we wanna show the `<ViewQuestions />` button unles...
       * if the assignment is not graded but the user is out of time, we want them submit the assignment
       * in order for them to view the questions
       */
      !isAnswerable &&
      !(!isGraded && isOutOfTime)
    ) {
      actions.push(<ViewQuestions onClick={onDismissDetails} disabled={!isGraded} />);
    }

    return (
      <StagingDetails.Footer>
        {actions.map((action, i) => {
          const isOnlyChild = actions.length === 1;
          const isLastChild = i + 1 === actions.length;
          return React.cloneElement(action, {
            color: isOnlyChild || isLastChild ? 'primary' : 'secondary',
            disabled: action.props.disabled || isActionPending,
            key: i
          });
        })}
      </StagingDetails.Footer>
    );
  }
}

const StartAssignment = ({onInit, onEnd, assignment, ...rest}) => {
  return (
    <Button
      {...rest}
      onClick={async () => {
        onInit();
        await startAssignment({assignment});
        onEnd();
      }}
    >
      Start assignment
    </Button>
  );
};

StartAssignment.propTypes = {
  onInit: PropTypes.func,
  onEnd: PropTypes.func,
  assignment: assignmentPropType
};

const Continue = (props) => {
  return <Button {...props}>Continue</Button>;
};

const ViewQuestions = ({disabled, ...rest}) => {
  return (
    <WithTooltip enabled={disabled} content='Results will be available after the due date passes.'>
      <Button disabled={disabled} {...rest}>
        View questions
      </Button>
    </WithTooltip>
  );
};

ViewQuestions.propTypes = {
  disabled: PropTypes.bool
};
